import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Check, X, ArrowLeft, Tag, Loader2, Star, TrendingUp, Shield, Smartphone, Clock, DollarSign } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

// Competitor comparison data based on research
// MyJobTools pricing is dynamic and will be updated from fetched plans
const getCompetitors = (tiers) => {
  const freeTier = tiers.find(t => t.id === 'free');
  const soloTier = tiers.find(t => t.id === 'solo');
  const crewTier = tiers.find(t => t.id === 'crew');
  
  // Build features dynamically based on what's enabled across plans
  const hasNativeSms = soloTier?.has_native_sms || crewTier?.has_native_sms;
  const hasNativeEmail = soloTier?.has_native_email || crewTier?.has_native_email;
  const hasAutoReminders = soloTier?.has_auto_reminders || crewTier?.has_auto_reminders;
  const hasRecurring = soloTier?.has_recurring || crewTier?.has_recurring;
  const hasCrm = soloTier?.has_crm || crewTier?.has_crm;
  
  return [
    {
      name: 'MyJobTools',
      logo: '/favicon.png',
      highlight: true,
      pricing: {
        starts: freeTier?.price || 'Free',
        solo: soloTier?.price ? `${soloTier.price}/mo` : '$19/mo',
        team: crewTier?.price ? `${crewTier.price}/mo` : '$49/mo'
      },
      features: {
        freeOption: true,
        unlimitedJobs: true, // on paid plans
        mobileFirst: true,
        oneThumbDesign: true,
        quickQuotes: true,
        smsReminders: true,
        nativeSms: hasNativeSms,
        nativeEmail: hasNativeEmail,
        autoReminders: hasAutoReminders,
        venmoPayments: true,
        stripePayments: true,
        expenseTracking: true,
        mileageTracking: true,
        teamManagement: true,
        clientPortal: true,
        pdfInvoices: true,
        recurringJobs: hasRecurring,
        clientCrm: hasCrm,
        easySetup: '60 seconds',
        support: 'Email & Chat'
      }
    },
    {
      name: 'Jobber',
      logo: null,
      pricing: {
        starts: '$39/mo',
        solo: '$39/mo',
        team: '$119-199/mo'
      },
      features: {
        freeOption: false,
        unlimitedJobs: true,
        mobileFirst: false,
        oneThumbDesign: false,
        quickQuotes: true,
        smsReminders: true,
        nativeSms: true,
        nativeEmail: true,
        autoReminders: true,
        venmoPayments: false,
        stripePayments: true,
        expenseTracking: true,
        mileageTracking: true,
        teamManagement: true,
        clientPortal: true,
        pdfInvoices: true,
        recurringJobs: true,
        clientCrm: true,
        easySetup: '1-2 hours',
        support: 'Phone & Email'
      }
    },
    {
      name: 'Housecall Pro',
      logo: null,
      pricing: {
        starts: '$59/mo',
        solo: '$59/mo',
        team: '$149-299/mo'
      },
      features: {
        freeOption: false,
        unlimitedJobs: true,
        mobileFirst: false,
        oneThumbDesign: false,
        quickQuotes: true,
        smsReminders: true,
        nativeSms: true,
        nativeEmail: true,
        autoReminders: true,
        venmoPayments: false,
        stripePayments: true,
        expenseTracking: true,
        mileageTracking: false,
        teamManagement: true,
        clientPortal: true,
        pdfInvoices: true,
        recurringJobs: true,
        clientCrm: true,
        easySetup: '1-2 hours',
        support: 'Phone & Email'
      }
    },
    {
      name: 'Workiz',
      logo: null,
      pricing: {
        starts: 'Free (limited)',
        solo: '$55/mo',
        team: '$149-299/mo'
      },
      features: {
        freeOption: true,
        unlimitedJobs: false, // limited on free
        mobileFirst: false,
        oneThumbDesign: false,
        quickQuotes: true,
        smsReminders: true,
        nativeSms: false,
        nativeEmail: true,
        autoReminders: true,
        venmoPayments: false,
        stripePayments: true,
        expenseTracking: false,
        mileageTracking: false,
        teamManagement: true,
        clientPortal: true,
        pdfInvoices: true,
        recurringJobs: true,
        clientCrm: true,
        easySetup: '1-2 hours',
        support: 'Phone & Email'
      }
    },
    {
      name: 'FieldPulse',
      logo: null,
      pricing: {
        starts: '$99/mo',
        solo: '$99/mo',
        team: '$189-329/mo'
      },
      features: {
        freeOption: false,
        unlimitedJobs: true,
        mobileFirst: false,
        oneThumbDesign: false,
        quickQuotes: true,
        smsReminders: true,
        nativeSms: true,
        nativeEmail: true,
        autoReminders: true,
        venmoPayments: false,
        stripePayments: true,
        expenseTracking: true,
        mileageTracking: false,
        teamManagement: true,
        clientPortal: true,
        pdfInvoices: true,
        recurringJobs: true,
        clientCrm: true,
        easySetup: 'Requires training',
        support: 'Phone & Email'
      }
    }
  ];
};

const comparisonFeatures = [
  { key: 'freeOption', label: 'Free Plan Available', important: true },
  { key: 'oneThumbDesign', label: 'One-Thumb Mobile Design', important: true },
  { key: 'quickQuotes', label: '30-Second Quotes' },
  { key: 'smsReminders', label: 'SMS Reminders' },
  { key: 'nativeSms', label: 'Native SMS (In-App)', important: true },
  { key: 'nativeEmail', label: 'In-App Email' },
  { key: 'autoReminders', label: 'Auto Appointment Reminders', important: true },
  { key: 'venmoPayments', label: 'Venmo/CashApp Support', important: true },
  { key: 'stripePayments', label: 'Card Payments' },
  { key: 'expenseTracking', label: 'Expense Tracking' },
  { key: 'mileageTracking', label: 'Mileage Tracking' },
  { key: 'teamManagement', label: 'Team Management' },
  { key: 'clientPortal', label: 'Customer Portal' },
  { key: 'pdfInvoices', label: 'PDF Invoices' },
  { key: 'recurringJobs', label: 'Recurring Jobs' },
  { key: 'clientCrm', label: 'Client Management (CRM)' }
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format job limit display
const formatJobsLimit = (limit) => {
  if (limit === null || limit === undefined || limit >= 9999) {
    return 'Unlimited';
  }
  return `${limit}`;
};

// Helper to format expense limit display
const formatExpensesLimit = (limit) => {
  if (limit === null || limit === undefined || limit >= 9999) {
    return 'Unlimited';
  }
  return `${limit}`;
};

// Helper to format message limit display
const formatMessageLimit = (limit) => {
  if (limit === null || limit === undefined) {
    return 'Unlimited';
  }
  return `${limit}/mo`;
};

// Build features dynamically from plan limits
const getFeatures = (plans) => {
  const freePlan = plans.find(p => p.id === 'free');
  const soloPlan = plans.find(p => p.id === 'solo');
  const crewPlan = plans.find(p => p.id === 'crew');
  
  // Get limits with defaults (API returns flat structure, not nested under limits)
  const freeJobsLimit = freePlan?.max_jobs_per_month ?? freePlan?.limits?.max_jobs_per_month;
  const soloJobsLimit = soloPlan?.max_jobs_per_month ?? soloPlan?.limits?.max_jobs_per_month;
  const crewJobsLimit = crewPlan?.max_jobs_per_month ?? crewPlan?.limits?.max_jobs_per_month;
  
  const freeTeamLimit = freePlan?.max_team_members ?? freePlan?.limits?.max_team_members ?? 1;
  const soloTeamLimit = soloPlan?.max_team_members ?? soloPlan?.limits?.max_team_members ?? 1;
  const crewTeamLimit = crewPlan?.max_team_members ?? crewPlan?.limits?.max_team_members ?? 10;
  
  const freeExpenseLimit = freePlan?.max_expenses_per_month ?? freePlan?.limits?.max_expenses_per_month;
  const soloExpenseLimit = soloPlan?.max_expenses_per_month ?? soloPlan?.limits?.max_expenses_per_month;
  const crewExpenseLimit = crewPlan?.max_expenses_per_month ?? crewPlan?.limits?.max_expenses_per_month;
  
  // Email and SMS limits
  const freeEmailLimit = freePlan?.max_emails_per_month;
  const soloEmailLimit = soloPlan?.max_emails_per_month;
  const crewEmailLimit = crewPlan?.max_emails_per_month;
  
  const freeSmsLimit = freePlan?.max_sms_per_month;
  const soloSmsLimit = soloPlan?.max_sms_per_month;
  const crewSmsLimit = crewPlan?.max_sms_per_month;
  
  return [
    { 
      name: 'Jobs per month', 
      free: formatJobsLimit(freeJobsLimit), 
      solo: formatJobsLimit(soloJobsLimit), 
      crew: formatJobsLimit(crewJobsLimit) 
    },
    { 
      name: 'Team members', 
      free: `${freeTeamLimit}`, 
      solo: `${soloTeamLimit}`, 
      crew: crewTeamLimit > 1 ? `Up to ${crewTeamLimit}` : `${crewTeamLimit}` 
    },
    { 
      name: 'Expenses per month', 
      free: formatExpensesLimit(freeExpenseLimit), 
      solo: formatExpensesLimit(soloExpenseLimit), 
      crew: formatExpensesLimit(crewExpenseLimit) 
    },
    { name: 'Quote & Invoice', free: true, solo: true, crew: true },
    { name: 'SMS reminders (external)', free: true, solo: true, crew: true },
    { 
      name: 'Native SMS (Twilio)', 
      free: freePlan?.has_native_sms ?? freePlan?.limits?.has_native_sms ?? false, 
      solo: soloPlan?.has_native_sms ?? soloPlan?.limits?.has_native_sms ?? false, 
      crew: crewPlan?.has_native_sms ?? crewPlan?.limits?.has_native_sms ?? true 
    },
    { 
      name: 'SMS per month', 
      free: freePlan?.has_native_sms ? formatMessageLimit(freeSmsLimit) : '—',
      solo: soloPlan?.has_native_sms ? formatMessageLimit(soloSmsLimit) : '—',
      crew: crewPlan?.has_native_sms ? formatMessageLimit(crewSmsLimit) : '—'
    },
    { 
      name: 'In-app Email', 
      free: freePlan?.has_native_email ?? freePlan?.limits?.has_native_email ?? false, 
      solo: soloPlan?.has_native_email ?? soloPlan?.limits?.has_native_email ?? false, 
      crew: crewPlan?.has_native_email ?? crewPlan?.limits?.has_native_email ?? true 
    },
    { 
      name: 'Emails per month', 
      free: freePlan?.has_native_email ? formatMessageLimit(freeEmailLimit) : '—',
      solo: soloPlan?.has_native_email ? formatMessageLimit(soloEmailLimit) : '—',
      crew: crewPlan?.has_native_email ? formatMessageLimit(crewEmailLimit) : '—'
    },
    { 
      name: 'Auto appointment reminders', 
      free: freePlan?.has_auto_reminders ?? freePlan?.limits?.has_auto_reminders ?? false, 
      solo: soloPlan?.has_auto_reminders ?? soloPlan?.limits?.has_auto_reminders ?? false, 
      crew: crewPlan?.has_auto_reminders ?? crewPlan?.limits?.has_auto_reminders ?? true 
    },
    { name: 'Customer portal', free: true, solo: true, crew: true },
    { name: 'Job scheduling', free: true, solo: true, crew: true },
    { name: 'Visits calendar', free: true, solo: true, crew: true },
    { name: 'Venmo/CashApp payments', free: true, solo: true, crew: true },
    { 
      name: 'Mileage tracking', 
      free: freePlan?.has_mileage ?? freePlan?.limits?.has_mileage ?? false, 
      solo: soloPlan?.has_mileage ?? soloPlan?.limits?.has_mileage ?? true, 
      crew: crewPlan?.has_mileage ?? crewPlan?.limits?.has_mileage ?? true 
    },
    { 
      name: 'Receipt photos', 
      free: freePlan?.has_receipt_photos ?? freePlan?.limits?.has_receipt_photos ?? false, 
      solo: soloPlan?.has_receipt_photos ?? soloPlan?.limits?.has_receipt_photos ?? true, 
      crew: crewPlan?.has_receipt_photos ?? crewPlan?.limits?.has_receipt_photos ?? true 
    },
    { 
      name: 'Billable expenses', 
      free: freePlan?.has_billable_expenses ?? freePlan?.limits?.has_billable_expenses ?? false, 
      solo: soloPlan?.has_billable_expenses ?? soloPlan?.limits?.has_billable_expenses ?? true, 
      crew: crewPlan?.has_billable_expenses ?? crewPlan?.limits?.has_billable_expenses ?? true 
    },
    { name: 'Note templates', free: false, solo: true, crew: true },
    { name: 'Job profitability reports', free: false, solo: true, crew: true },
    { 
      name: 'Custom logo on invoices', 
      free: freePlan?.has_logo ?? freePlan?.limits?.has_logo ?? false, 
      solo: soloPlan?.has_logo ?? soloPlan?.limits?.has_logo ?? true, 
      crew: crewPlan?.has_logo ?? crewPlan?.limits?.has_logo ?? true 
    },
    { name: 'Tax calculations', free: false, solo: true, crew: true },
    { name: 'CSV export', free: false, solo: true, crew: true },
    { 
      name: 'Stripe payments', 
      free: freePlan?.has_stripe ?? freePlan?.limits?.has_stripe ?? false, 
      solo: soloPlan?.has_stripe ?? soloPlan?.limits?.has_stripe ?? true, 
      crew: crewPlan?.has_stripe ?? crewPlan?.limits?.has_stripe ?? true 
    },
    { name: 'Team management', free: false, solo: false, crew: true },
    { name: 'Shared calendar', free: false, solo: false, crew: true },
    { name: 'Job assignment', free: false, solo: false, crew: true },
    { 
      name: 'Recurring appointments', 
      free: freePlan?.has_recurring ?? freePlan?.limits?.has_recurring ?? false, 
      solo: soloPlan?.has_recurring ?? soloPlan?.limits?.has_recurring ?? false, 
      crew: crewPlan?.has_recurring ?? crewPlan?.limits?.has_recurring ?? true 
    },
    { 
      name: 'Client Management (CRM)', 
      free: freePlan?.has_crm ?? false, 
      solo: soloPlan?.has_crm ?? false, 
      crew: crewPlan?.has_crm ?? true 
    },
  ];
};

// Default tiers fallback
const getDefaultTiers = () => [
  { id: 'free', name: 'Side Hustle', price: 'Free', priceNum: 0, description: 'Perfect for weekend warriors' },
  { id: 'solo', name: 'Solo Pro', price: '$19', priceNum: 19, description: 'For full-time contractors', popular: true },
  { id: 'crew', name: 'Crew', price: '$49', priceNum: 49, description: 'For growing teams' },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [couponCode, setCouponCode] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  
  // Dynamic pricing state
  const [tiers, setTiers] = useState(getDefaultTiers());
  const [features, setFeatures] = useState(getFeatures([]));
  const [competitors, setCompetitors] = useState(getCompetitors(getDefaultTiers()));
  const [loadingPlans, setLoadingPlans] = useState(true);
  const hasTracked = useRef(false);

  // Track page visit for analytics (with guard against StrictMode double-fire)
  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    
    const trackVisit = async () => {
      try {
        await axios.post(`${API}/analytics/track`, {
          page: 'pricing',
          referrer: document.referrer || null,
          user_agent: navigator.userAgent
        });
      } catch (err) {
        // Silently fail - analytics shouldn't break the app
        console.debug('Analytics tracking failed:', err);
      }
    };
    trackVisit();
  }, []);

  // Fetch pricing plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API}/plans`);
        // Handle both array and {plans: [...]} response formats
        const plans = response.data.plans || response.data || [];
        
        // Plan display names and descriptions
        const planMeta = {
          free: { name: 'Side Hustle', description: 'Perfect for weekend warriors' },
          solo: { name: 'Solo Pro', description: 'For full-time contractors', popular: true },
          crew: { name: 'Crew', description: 'For growing teams' }
        };
        
        // Convert API response to tiers format
        const formattedTiers = plans.map(plan => ({
          id: plan.id,
          name: planMeta[plan.id]?.name || plan.id,
          price: plan.price === 0 ? 'Free' : `$${Math.round(plan.price)}`,
          priceNum: plan.price,
          description: planMeta[plan.id]?.description || '',
          popular: planMeta[plan.id]?.popular || false,
          limits: {
            max_jobs_per_month: plan.max_jobs_per_month,
            max_team_members: plan.max_team_members,
            max_expenses_per_month: plan.max_expenses_per_month,
            has_logo: plan.has_logo,
            has_stripe: plan.has_stripe,
            has_receipt_photos: plan.has_receipt_photos,
            has_mileage: plan.has_mileage,
            has_billable_expenses: plan.has_billable_expenses,
            has_native_sms: plan.has_native_sms,
            has_native_email: plan.has_native_email,
            has_auto_reminders: plan.has_auto_reminders
          }
        }));
        
        setTiers(formattedTiers);
        setFeatures(getFeatures(plans));
        setCompetitors(getCompetitors(formattedTiers));
      } catch (error) {
        console.error('Error fetching plans:', error);
        // Keep default tiers on error
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  const handleValidateCoupon = async (planId) => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    try {
      const response = await axios.post(`${API}/coupons/validate?code=${couponCode}&plan=${planId}`);
      setAppliedCoupon({ ...response.data, plan: planId });
      toast.success(`${response.data.discount_percent}% discount applied!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!appliedCoupon || !user) return;
    
    setApplyingCoupon(true);
    try {
      // Create Stripe checkout with coupon
      const response = await axios.post(`${API}/stripe/checkout/subscription`, {
        tier: appliedCoupon.plan,
        origin_url: window.location.origin,
        coupon_code: appliedCoupon.code
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setApplyingCoupon(false);
    }
  };

  const handleSelectPlan = async (tierId) => {
    if (!user) {
      // Not logged in - redirect to register
      navigate('/register');
      return;
    }
    
    if (tierId === 'free') {
      // Free plan doesn't need checkout
      toast.info('You are already on the Free plan');
      return;
    }
    
    // Create Stripe checkout session
    try {
      const response = await axios.post(`${API}/stripe/checkout/subscription`, {
        tier: tierId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const getDiscountedPrice = (tierId, originalPrice) => {
    if (appliedCoupon && appliedCoupon.plan === tierId) {
      return appliedCoupon.discounted_price;
    }
    return originalPrice;
  };

  // Show loading spinner while fetching plans
  if (loadingPlans) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5F00]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Pricing</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Coupon Code Input */}
        {user && (
          <div className="bg-gradient-to-r from-[#FF5F00]/10 to-purple-500/10 rounded-2xl p-6 mb-8 border-2 border-dashed border-[#FF5F00]/30">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-5 h-5 text-[#FF5F00]" />
              <h3 className="font-bold text-slate-900">Have a coupon code?</h3>
            </div>
            <div className="flex gap-2">
              <Input
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value.toUpperCase());
                  setAppliedCoupon(null);
                }}
                placeholder="Enter code (e.g., SUMMER20)"
                className="flex-1 h-12 border-2 border-slate-300 font-mono uppercase"
                data-testid="coupon-code-input"
              />
              <Button
                onClick={() => handleValidateCoupon('solo')}
                disabled={!couponCode.trim() || validatingCoupon}
                className="h-12 bg-[#FF5F00] hover:bg-orange-600 px-6"
                data-testid="validate-coupon-btn"
              >
                {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
              </Button>
            </div>
            {appliedCoupon && (
              <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="font-bold text-emerald-700">
                    {appliedCoupon.discount_percent}% OFF Applied!
                  </p>
                  <p className="text-sm text-emerald-600">
                    ${appliedCoupon.original_price} → ${appliedCoupon.discounted_price}/mo (Save ${appliedCoupon.savings}/mo)
                  </p>
                </div>
                <Button
                  onClick={handleApplyCoupon}
                  disabled={applyingCoupon}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="apply-coupon-upgrade-btn"
                >
                  {applyingCoupon ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Upgrade Now
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Plan Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {tiers.map((tier) => {
            const discountedPrice = getDiscountedPrice(tier.id, tier.priceNum);
            const hasDiscount = appliedCoupon && appliedCoupon.plan === tier.id;
            
            return (
              <div
                key={tier.id}
                className={`bg-white rounded-2xl p-6 border-2 relative ${
                  tier.popular ? 'border-[#FF5F00] shadow-lg' : 'border-slate-200'
                } ${hasDiscount ? 'ring-2 ring-emerald-500' : ''}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF5F00] text-white text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                {hasDiscount && (
                  <div className="absolute -top-3 right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {appliedCoupon.discount_percent}% OFF
                  </div>
                )}
                
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-1">{tier.name}</h3>
                <p className="text-slate-500 text-sm mb-4">{tier.description}</p>
                
                <div className="mb-4">
                  {hasDiscount ? (
                    <>
                      <span className="font-heading text-4xl font-bold text-emerald-600">${discountedPrice}</span>
                      <span className="text-slate-400 line-through ml-2">${tier.priceNum}</span>
                      <span className="text-slate-500">/month</span>
                    </>
                  ) : (
                    <>
                      <span className="font-heading text-4xl font-bold text-slate-900">{tier.price}</span>
                      {tier.priceNum > 0 && <span className="text-slate-500">/month</span>}
                    </>
                  )}
                </div>
                
                <Button
                  onClick={() => hasDiscount ? handleApplyCoupon() : handleSelectPlan(tier.id)}
                  disabled={applyingCoupon && hasDiscount}
                  className={`w-full h-12 font-bold ${
                    hasDiscount
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : tier.popular
                      ? 'bg-[#FF5F00] hover:bg-orange-600 text-white'
                      : 'bg-slate-900 hover:bg-slate-800 text-white'
                  }`}
                >
                  {applyingCoupon && hasDiscount ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  {user?.subscription_tier === tier.id 
                    ? 'Current Plan' 
                    : hasDiscount 
                    ? `Start Trial - $${discountedPrice}/mo after`
                    : !user
                    ? (tier.priceNum === 0 ? 'Start Free' : 'Start 14-Day Free Trial')
                    : tier.priceNum === 0 
                    ? 'Downgrade' 
                    : 'Start 14-Day Free Trial'}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Feature Comparison Table */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-heading text-xl font-bold text-slate-900">Feature Comparison</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-3 px-4 font-bold text-slate-700">Feature</th>
                  {tiers.map((tier) => (
                    <th key={tier.id} className="text-center py-3 px-4 font-bold text-slate-700">
                      {tier.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="py-3 px-4 text-slate-700">{feature.name}</td>
                    {['free', 'solo', 'crew'].map((tierId) => {
                      const value = feature[tierId];
                      return (
                        <td key={tierId} className="text-center py-3 px-4">
                          {typeof value === 'boolean' ? (
                            value ? (
                              <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                            ) : (
                              <X className="w-5 h-5 text-slate-300 mx-auto" />
                            )
                          ) : (
                            <span className="font-medium text-slate-900">{value}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Competitor Comparison Section */}
        <div className="mt-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border-2 border-slate-700 p-6 overflow-hidden" data-testid="competitor-comparison">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-[#FF5F00]/20 text-[#FF5F00] px-4 py-2 rounded-full text-sm font-bold mb-3">
              <TrendingUp className="w-4 h-4" />
              How We Compare
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">
              Built Different. Priced Better.
            </h2>
            <p className="text-slate-400 text-sm max-w-xl mx-auto">
              See how MyJobTools stacks up against the big guys. We focus on what contractors actually need.
            </p>
          </div>

          {/* Pricing Comparison Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            {competitors.map((comp, idx) => (
              <div 
                key={comp.name}
                className={`rounded-xl p-4 ${
                  comp.highlight 
                    ? 'bg-[#FF5F00] text-white ring-2 ring-[#FF5F00] ring-offset-2 ring-offset-slate-900' 
                    : 'bg-slate-700/50 text-slate-300'
                }`}
              >
                <div className="text-center">
                  {comp.highlight && (
                    <span className="inline-flex items-center gap-1 bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full mb-2">
                      <Star className="w-3 h-3 fill-current" />
                      BEST VALUE
                    </span>
                  )}
                  <h3 className={`font-bold text-sm mb-1 ${comp.highlight ? 'text-white' : 'text-white'}`}>
                    {comp.name}
                  </h3>
                  <p className={`text-xs mb-2 ${comp.highlight ? 'text-white/80' : 'text-slate-400'}`}>
                    starts at
                  </p>
                  <p className={`font-heading text-2xl font-bold ${comp.highlight ? 'text-white' : 'text-slate-100'}`}>
                    {comp.pricing.starts}
                  </p>
                  <p className={`text-xs mt-1 ${comp.highlight ? 'text-white/70' : 'text-slate-500'}`}>
                    Team: {comp.pricing.team}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Feature Comparison Table */}
          <div className="bg-slate-800/50 rounded-xl overflow-hidden border border-slate-700">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 font-bold text-slate-300 bg-slate-800/80">Feature</th>
                    {competitors.map((comp) => (
                      <th 
                        key={comp.name} 
                        className={`text-center py-3 px-3 font-bold ${
                          comp.highlight ? 'bg-[#FF5F00]/20 text-[#FF5F00]' : 'text-slate-400'
                        }`}
                      >
                        {comp.name.split(' ')[0]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((feature, index) => (
                    <tr key={feature.key} className={`border-b border-slate-700/50 ${index % 2 === 0 ? 'bg-slate-800/30' : ''}`}>
                      <td className={`py-2.5 px-4 text-slate-300 ${feature.important ? 'font-medium' : ''}`}>
                        {feature.label}
                        {feature.important && <span className="text-[#FF5F00] ml-1">*</span>}
                      </td>
                      {competitors.map((comp) => {
                        const value = comp.features[feature.key];
                        return (
                          <td 
                            key={`${comp.name}-${feature.key}`} 
                            className={`text-center py-2.5 px-3 ${comp.highlight ? 'bg-[#FF5F00]/5' : ''}`}
                          >
                            {typeof value === 'boolean' ? (
                              value ? (
                                <Check className={`w-5 h-5 mx-auto ${comp.highlight ? 'text-[#FF5F00]' : 'text-emerald-500'}`} />
                              ) : (
                                <X className="w-5 h-5 text-slate-600 mx-auto" />
                              )
                            ) : (
                              <span className={`text-xs ${comp.highlight ? 'text-[#FF5F00] font-medium' : 'text-slate-400'}`}>
                                {value}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Key Differentiators */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <div className="bg-slate-700/30 rounded-xl p-4 border border-slate-700/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#FF5F00]/20 rounded-lg flex items-center justify-center">
                  <Smartphone className="w-4 h-4 text-[#FF5F00]" />
                </div>
                <h4 className="font-bold text-white text-sm">Mobile-First Design</h4>
              </div>
              <p className="text-slate-400 text-xs">
                Built for one-thumb operation. Every button reachable while holding a wrench.
              </p>
            </div>
            <div className="bg-slate-700/30 rounded-xl p-4 border border-slate-700/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#FF5F00]/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-4 h-4 text-[#FF5F00]" />
                </div>
                <h4 className="font-bold text-white text-sm">60-Second Setup</h4>
              </div>
              <p className="text-slate-400 text-xs">
                No training required. Start sending quotes today, not next week.
              </p>
            </div>
            <div className="bg-slate-700/30 rounded-xl p-4 border border-slate-700/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#FF5F00]/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-[#FF5F00]" />
                </div>
                <h4 className="font-bold text-white text-sm">Venmo & CashApp</h4>
              </div>
              <p className="text-slate-400 text-xs">
                Get paid instantly with apps your customers already use. No fees.
              </p>
            </div>
          </div>

          <p className="text-slate-500 text-xs text-center mt-4">
            * Key differentiators that set MyJobTools apart. Pricing accurate as of 2025. Check competitor sites for current rates.
          </p>
        </div>

        {/* FAQ */}
        <div className="mt-8 bg-white rounded-2xl border-2 border-slate-200 p-6">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-bold text-slate-900 mb-1">Can I upgrade anytime?</h3>
              <p className="text-slate-600 text-sm">Yes! You can upgrade from Free to Solo or Crew at any time. Your new features activate immediately.</p>
            </div>
            
            <div>
              <h3 className="font-bold text-slate-900 mb-1">What payment methods do you accept?</h3>
              <p className="text-slate-600 text-sm">We use Stripe for secure payments. You can pay with any major credit card.</p>
            </div>
            
            <div>
              <h3 className="font-bold text-slate-900 mb-1">Can I cancel anytime?</h3>
              <p className="text-slate-600 text-sm">Yes, you can cancel your subscription anytime from your Profile. You'll keep access until the end of your billing period.</p>
            </div>
            
            <div>
              <h3 className="font-bold text-slate-900 mb-1">How do my customers pay me?</h3>
              <p className="text-slate-600 text-sm">Free users can use Venmo or CashApp links. Solo and Crew users can also connect their own Stripe account to accept card payments directly.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
